import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { popularCompanies } from '../data/popularCompanies';
import { mockVacancies } from '../data/mockVacancies';
import { getCompanyColor, formatSalaryRange } from '../utils/companyHelpers';
import VacancyCard from '../components/VacancyCard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface CompanyProfileProps {
  darkMode: boolean;
}

const CompanyProfile: React.FC<CompanyProfileProps> = ({ darkMode }) => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();

  const company = useMemo(
    () => popularCompanies.find(c => c.id === companyId),
    [companyId]
  );

  const vacancies = useMemo(
    () => company ? mockVacancies.filter(v => v.company_name === company.name) : [],
    [company]
  );

  if (!company) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <button className="btn-back" onClick={() => navigate('/companies')}>
          <ArrowBackIcon sx={{ fontSize: 18 }} /> Back to Companies
        </button>
        <div className="empty-state">
          <h3>Company not found</h3>
          <p>This company doesn't exist in our directory.</p>
        </div>
      </div>
    );
  }

  const color = getCompanyColor(company.id);

  return (
    <div className={`page-container ${darkMode ? 'dark' : ''}`}>
      <button className="btn-back" onClick={() => navigate('/companies')}>
        <ArrowBackIcon sx={{ fontSize: 18 }} /> Back to Companies
      </button>

      <div className="company-profile">
        <div className="company-profile-header">
          <div className="company-logo-placeholder large" style={{ background: color }}>
            {company.name.charAt(0)}
          </div>
          <div className="company-profile-info">
            <h1>{company.name}</h1>
            <span className="company-card-industry">{company.industry}</span>
          </div>
        </div>

        <div className="company-stats-grid">
          <div className="company-stat">
            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            <div>
              <span className="stat-label">Headquarters</span>
              <span className="stat-value">{company.headquarters}</span>
            </div>
          </div>
          <div className="company-stat">
            <PeopleOutlineIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            <div>
              <span className="stat-label">Employees</span>
              <span className="stat-value">{company.employee_count}</span>
            </div>
          </div>
          <div className="company-stat">
            <StarIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
            <div>
              <span className="stat-label">Glassdoor Rating</span>
              <span className="stat-value">{company.glassdoor_rating} / 5.0</span>
            </div>
          </div>
          <div className="company-stat">
            <div style={{ fontSize: 18, lineHeight: '18px' }}>$</div>
            <div>
              <span className="stat-label">Salary Range</span>
              <span className="stat-value">
                {formatSalaryRange(company.salary_range.min, company.salary_range.max, company.salary_range.currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="company-card-tags" style={{ marginBottom: 24 }}>
          {company.tags.map(tag => (
            <span key={tag} className="company-tag">{tag}</span>
          ))}
        </div>

        <div className="company-link-buttons">
          <a
            href={company.glassdoor_url}
            target="_blank"
            rel="noopener noreferrer"
            className="company-link-btn"
          >
            View on Glassdoor <OpenInNewIcon sx={{ fontSize: 16 }} />
          </a>
          <a
            href={company.levels_url}
            target="_blank"
            rel="noopener noreferrer"
            className="company-link-btn"
          >
            View on Levels.fyi <OpenInNewIcon sx={{ fontSize: 16 }} />
          </a>
        </div>

        <div className="company-vacancies-section">
          <h2>Open Vacancies ({vacancies.length})</h2>
          {vacancies.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px 0' }}>
              <p>No open positions yet. Check back later!</p>
            </div>
          ) : (
            <div className="vacancy-grid">
              {vacancies.map(v => (
                <VacancyCard
                  key={v.id}
                  vacancy={v}
                  onClick={(id) => navigate(`/vacancies/${id}`)}
                  darkMode={darkMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
